import React, { useEffect, useState } from 'react';
import { Navigate, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import Header from '../components/Header';
import Footer from '../components/Footer';
import Breadcrumbs from '../components/Breadcrumbs';
import WhatsAppWidget from '../components/WhatsAppWidget';
import { CATEGORIES } from '../data/categories';
import cityPages from '../data/cityPages.json';
import { ArrowRight, CheckCircle2, MapPin, Phone } from 'lucide-react';

export default function CityPage({ slug }) {
  const [scrolled, setScrolled] = useState(false);
  const data = cityPages.find((c) => c.slug === slug);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', onScroll);
    window.scrollTo(0, 0);
    return () => window.removeEventListener('scroll', onScroll);
  }, [slug]);

  if (!data) return <Navigate to="/" replace />;

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: data.faq.map((f) => ({ '@type': 'Question', name: f.q, acceptedAnswer: { '@type': 'Answer', text: f.a } })),
  };

  return (
    <div className="min-h-screen bg-white">
      <Helmet>
        <title>{data.title}</title>
        <meta name="description" content={data.metaDescription} />
        <link rel="canonical" href={`https://harpalyk.com/cleaning-robots-in-${data.slug}`} />
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
      </Helmet>
      <Header scrolled={scrolled} />
      <main className="pt-16 lg:pt-[72px]" data-testid={`city-page-${data.slug}`}>
        <Breadcrumbs items={[{ label: `Cleaning Robots in ${data.city}` }]} />

        <section className="py-16 lg:py-24 px-6 lg:px-12 bg-gradient-to-b from-[#F8F9FA] to-white">
          <div className="max-w-4xl mx-auto">
            <p className="text-xs font-semibold tracking-[0.25em] text-emerald-600 uppercase mb-4 inline-flex items-center gap-2">
              <MapPin size={14} /> {data.city}, {data.state}
            </p>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight text-gray-900 mb-8">{data.h1}</h1>
            {data.intro.map((p, i) => (
              <p key={i} className="text-base md:text-lg text-gray-600 leading-relaxed mb-5">{p}</p>
            ))}
            <div className="flex flex-col sm:flex-row gap-4 mt-8">
              <a href="/#contact" data-testid="city-demo-cta" className="inline-flex items-center justify-center gap-2 rounded-full bg-gray-900 text-white px-8 py-4 text-base font-medium hover:bg-gray-700 transition-colors duration-300">
                Book a Free Demo in {data.city} <ArrowRight size={17} />
              </a>
              <a href="tel:+917011370322" className="inline-flex items-center justify-center gap-2 rounded-full border border-gray-300 text-gray-900 px-8 py-4 text-base font-medium hover:border-gray-900 transition-colors duration-300">
                <Phone size={16} /> +91-7011370322
              </a>
            </div>
          </div>
        </section>

        <section className="py-12 lg:py-16 px-6 lg:px-12">
          <div className="max-w-4xl mx-auto">
            <h2 className="font-heading text-2xl md:text-3xl font-semibold text-gray-900 mb-6">Where Our Robots Work in {data.city}</h2>
            <ul className="space-y-3">
              {data.useCases.map((u, i) => (
                <li key={i} className="flex items-start gap-3 text-gray-700">
                  <CheckCircle2 size={18} className="text-emerald-600 mt-0.5 flex-shrink-0" />
                  <span>{u}</span>
                </li>
              ))}
            </ul>
          </div>
        </section>

        <section className="py-12 lg:py-16 px-6 lg:px-12 bg-[#F8F9FA]">
          <div className="max-w-7xl mx-auto">
            <h2 className="font-heading text-2xl md:text-3xl font-semibold text-gray-900 mb-8">Robot Categories Available in {data.city}</h2>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-5">
              {CATEGORIES.map((cat) => (
                <Link key={cat.id} to={cat.path} className="group rounded-2xl border border-black/5 bg-white p-5 hover:shadow-lg hover:-translate-y-0.5 transition-[box-shadow,transform] duration-300">
                  <div className="h-28 rounded-xl overflow-hidden bg-[#F8F9FA] mb-4">
                    <img src={cat.image} alt={`Harpalyk ${cat.family} ${cat.name.toLowerCase()} in ${data.city}`} loading="lazy" className="w-full h-full object-contain p-2 group-hover:scale-105 transition-transform duration-500" />
                  </div>
                  <h3 className="font-heading text-sm font-semibold text-gray-900 mb-1">{cat.family} — {cat.name}</h3>
                  <span className="inline-flex items-center gap-1 text-xs font-medium text-emerald-600">Explore <ArrowRight size={12} /></span>
                </Link>
              ))}
            </div>
          </div>
        </section>

        <section className="py-12 lg:py-20 px-6 lg:px-12">
          <div className="max-w-4xl mx-auto">
            <h2 className="font-heading text-2xl md:text-3xl font-semibold text-gray-900 mb-8">Frequently Asked Questions — {data.city}</h2>
            <div className="space-y-4">
              {data.faq.map((f, i) => (
                <div key={i} className="rounded-2xl border border-black/5 bg-[#F8F9FA] p-6">
                  <h3 className="font-heading text-base font-semibold text-gray-900 mb-2">{f.q}</h3>
                  <p className="text-sm text-gray-600 leading-relaxed">{f.a}</p>
                </div>
              ))}
            </div>
            <div className="mt-10 text-sm text-gray-500">
              Also serving:{' '}
              {cityPages.filter((c) => c.slug !== data.slug).slice(0, 6).map((c, i, arr) => (
                <span key={c.slug}>
                  <Link to={`/cleaning-robots-in-${c.slug}`} className="text-emerald-700 hover:underline">{c.city}</Link>
                  {i < arr.length - 1 ? ' · ' : ''}
                </span>
              ))}
            </div>
          </div>
        </section>
      </main>
      <Footer />
      <WhatsAppWidget />
    </div>
  );
}
